import React, { useState, useEffect } from "react";
import { X, FileText, CheckCircle2, FileImage, ShieldAlert, Users, Sparkles, Gem } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { createReportJob } from "../api/reports";
import type { ExistingReport } from "../api/reports";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { isFreePlanUser } from "../utils/subscription";
import logo from "../assets/logo2.png";

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  domain: string;
  companyName: string;
  sector: string;
  sectorConfidence: "high" | "low";
  sectorConfirmed: boolean;
  existingReports: ExistingReport[];
  userRole: string;
  onStartJob: (job: any) => void;
  onConflict: (job: any, message: string) => void;
  onDownloadExisting: (reportId: string) => void;
}

export const ReportModal: React.FC<ReportModalProps> = ({
  isOpen, onClose, domain, companyName, sector, sectorConfidence, sectorConfirmed,
  existingReports, userRole, onStartJob, onConflict, onDownloadExisting
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isFree = isFreePlanUser(user);

  const [period, setPeriod] = useState("30D");
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const [includeExcerpts, setIncludeExcerpts] = useState(true);
  const [includeScreenshots, setIncludeScreenshots] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [remainingToday, setRemainingToday] = useState<number | null>(null);
  const [format, setFormat] = useState("pdf_v2");

  useEffect(() => {
    if (isOpen) {
      setIdempotencyKey(crypto.randomUUID ? crypto.randomUUID() : `key-${Date.now()}-${Math.random()}`);
      api.get("/report-center/jobs?scope=mine").then(res => {
        setRemainingToday(res.data.remaining_reports_today);
      }).catch(() => {});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const needsConfirmation = sectorConfidence === "low" && !sectorConfirmed;

  const handleStart = async () => {
    if (needsConfirmation) {
      toast.error("Please confirm the sector before generating a report.");
      return;
    }
    setSubmitting(true);
    try {
      const job = await createReportJob(domain, sector, period, format, idempotencyKey);
      onStartJob(job);
      toast.success("Generating report. Please wait...", {
        description: "Your executive intelligence report is being prepared.",
        duration: 4000
      });
      onClose();
    } catch (err: any) {
      if (err.response?.status === 409) {
        onConflict(err.response.data.active_job, err.response.data.detail || "A report is already being generated.");
        onClose();
      } else {
        const detail = err.response?.data?.detail;
        toast.error(typeof detail === 'string' ? detail : (detail?.message || "Failed to start report generation."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const periodLabels: Record<string, string> = {
    "7D": "Last 7 days",
    "30D": "Last 30 days",
    "90D": "Last 90 days",
    "LAST_QUARTER": "Last quarter"
  };

  const exactMatch = existingReports.find(r => r.domain === domain && r.period === period && r.sector === sector);

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "var(--card-bg)", borderRadius: "8px", width: "100%", maxWidth: "520px", padding: "24px", position: "relative", border: "1px solid var(--card-border)" }}>
        {/* Top Header with Brand Logo & Close Button */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
          <img 
            src={logo} 
            alt="Threatactix" 
            className="report-modal-logo"
            style={{ height: "30px", width: "auto", objectFit: "contain" }} 
          />
          <button 
            onClick={onClose} 
            style={{ background: "transparent", border: "none", color: "var(--color-text-subtle)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: "4px" }}
          >
            <X size={20} />
          </button>
        </div>

        <h2 style={{ fontSize: "19px", fontWeight: 700, margin: "0 0 6px 0", color: "var(--color-text)" }}>Generate Intelligence Report</h2>
        <p style={{ fontSize: "13px", color: "var(--color-text-muted)", margin: "0 0 16px 0" }}>
          Configure enterprise threat intelligence &amp; dark web exposure report for <strong>{domain}</strong>.
        </p>

        {isFree && (
          <div style={{
            background: "rgba(245, 183, 56, 0.08)",
            border: "1px solid rgba(245, 183, 56, 0.3)",
            padding: "12px 14px",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            margin: "0 0 20px 0"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Gem size={18} color="#f5b738" className="shrink-0" />
              <div>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "#f5b738" }}>
                  Business Pro &amp; Enterprise Feature
                </div>
                <div style={{ fontSize: "11px", color: "var(--color-text-muted)" }}>
                  Generating and downloading threat reports requires an upgraded subscription.
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                onClose();
                navigate("/subscription");
              }}
              style={{
                background: "#f5b738",
                color: "#78350E",
                border: "none",
                borderRadius: "6px",
                padding: "6px 14px",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
                whiteSpace: "nowrap"
              }}
            >
              Upgrade Plan
            </button>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "24px" }}>
          <div>
            <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "8px" }}>Company & Sector</div>
            <div style={{ background: "var(--color-bg)", padding: "12px", borderRadius: "6px", border: "1px solid var(--card-border)", fontSize: "13px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                <span style={{ color: "var(--color-text-muted)" }}>Company</span>
                <strong style={{ color: "var(--color-text)" }}>{companyName || domain}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "var(--color-text-muted)" }}>Sector</span>
                <strong style={{ color: "var(--color-text)", display: "flex", alignItems: "center", gap: "6px" }}>
                  {sector}
                  {needsConfirmation && <span style={{ color: "#e24b4a", fontSize: "10px" }}>(Needs confirmation)</span>}
                  {!needsConfirmation && <CheckCircle2 size={12} color="#2dd260" />}
                </strong>
              </div>
            </div>
          </div>

          <div>
            <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "8px" }}>Time Period</div>
            <select 
              value={period} 
              onChange={e => setPeriod(e.target.value)}
              style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid var(--card-border)", background: "var(--color-bg)", color: "var(--color-text)", fontSize: "13px" }}
            >
              {Object.entries(periodLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>

          <div>
             <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "8px" }}>Report Format</div>
             <select 
               value={format} 
               onChange={e => setFormat(e.target.value)}
               style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid var(--card-border)", background: "var(--color-bg)", color: "var(--color-text)", fontSize: "13px", marginBottom: "16px" }}
             >
               <option value="pdf_v2">PDF Premium (AI Executive Briefing &amp; Sector Intelligence)</option>
               <option value="docx">Word Document (DOCX)</option>
             </select>
             
             <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "8px" }}>Report Content</div>
             <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", cursor: "pointer", marginBottom: "8px" }}>
               <input type="checkbox" checked={includeExcerpts} onChange={e => setIncludeExcerpts(e.target.checked)} />
               Include evidence excerpts
             </label>
             {(userRole === "admin" || userRole === "analyst") && (
               <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", cursor: "pointer" }}>
                 <input type="checkbox" checked={includeScreenshots} onChange={e => setIncludeScreenshots(e.target.checked)} />
                 Include evidence screenshots
               </label>
             )}
          </div>
          
          <div style={{ background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.25)", padding: "12px", borderRadius: "8px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#6366F1", marginBottom: "8px", textTransform: "uppercase", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span>What's inside this report</span>
              <span style={{ fontSize: "9px", background: "rgba(99,102,241,0.15)", color: "#6366F1", padding: "2px 6px", borderRadius: "4px", fontWeight: 700, letterSpacing: "0.03em", display: "flex", alignItems: "center", gap: "3px" }}>
                <Sparkles size={10} /> AI SYNTHESIZED
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", fontSize: "12px", color: "var(--color-text)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}><ShieldAlert size={14} color="#6366F1"/> Sector Risk Posture</div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}><FileText size={14} color="#6366F1"/> AI Executive Briefing</div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}><Users size={14} color="#6366F1"/> Active Sector Cartels</div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}><FileImage size={14} color="#6366F1"/> Peer Incidents &amp; Evidence</div>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: "1px solid var(--card-border)", paddingTop: "16px" }}>
          <div style={{ fontSize: "11px", color: "var(--color-text-subtle)", display: "flex", flexDirection: "column", gap: "2px" }}>
            {remainingToday !== null && <span>{remainingToday} reports remaining today</span>}
            <span>Usually 1 to 2 minutes - PDF</span>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            {isFree ? (
              <button 
                onClick={() => {
                  onClose();
                  navigate("/subscription");
                }}
                style={{
                  padding: "10px 18px",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: 500,
                  background: "#f5b738",
                  color: "#78350E",
                  border: "1px solid #e0a328",
                  boxShadow: "0 2px 8px rgba(245, 183, 56, 0.25)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}
              >
                <Gem size={14} color="#78350E" className="stroke-[1.8]" />
                <span>Upgrade to download report</span>
              </button>
            ) : exactMatch ? (
              <>
                <button 
                  onClick={() => onDownloadExisting(exactMatch.id)}
                  style={{ padding: "10px 16px", borderRadius: "6px", fontSize: "13px", fontWeight: 600, background: "var(--color-surface-2)", color: "var(--color-text)", border: "1px solid var(--card-border)", cursor: "pointer" }}
                >
                  Open existing report
                </button>
                <button 
                  onClick={handleStart}
                  disabled={submitting || needsConfirmation}
                  style={{ padding: "10px 16px", borderRadius: "6px", fontSize: "13px", fontWeight: 600, background: "transparent", color: "#6366F1", border: "1px solid #6366F1", cursor: submitting || needsConfirmation ? "not-allowed" : "pointer", opacity: submitting || needsConfirmation ? 0.6 : 1 }}
                >
                  {submitting ? "Starting..." : "Regenerate"}
                </button>
              </>
            ) : (
              <button 
                onClick={handleStart}
                disabled={submitting || needsConfirmation}
                style={{ padding: "10px 16px", borderRadius: "6px", fontSize: "13px", fontWeight: 600, background: "#6366F1", color: "white", border: "none", cursor: submitting || needsConfirmation ? "not-allowed" : "pointer", opacity: submitting || needsConfirmation ? 0.6 : 1 }}
              >
                {submitting ? "Starting..." : "Start"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
